import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  notificationsApi,
  CreateNotificationDto,
  PreviewNotificationDto,
  NotificationType,
  AudienceType,
} from '../../api/notifications';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { ArrowLeft, Loader2, Eye, Send, Calendar, Users, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

interface CreateNotificationPageProps {
  isDark: boolean;
}

export default function CreateNotificationPage({ isDark }: CreateNotificationPageProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [sendNow, setSendNow] = useState(true);
  const [formData, setFormData] = useState<CreateNotificationDto>({
    title: '',
    body: '',
    type: NotificationType.SYSTEM,
    data: {},
    audience_filters: { audience_type: AudienceType.ALL },
    priority: 5,
    is_sponsored: false,
    is_premium: false,
  });
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  const handlePreview = async () => {
    if (!formData.title || !formData.body) {
      toast.error('Please fill in title and body');
      return;
    }
    setIsPreviewLoading(true);
    try {
      const previewDto: PreviewNotificationDto = {
        title: formData.title,
        body: formData.body,
        type: formData.type,
        data: formData.data,
        audience_filters: formData.audience_filters,
      };
      const data = await notificationsApi.preview(previewDto);
      setPreviewData(data);
      toast.success('Preview calculated successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to preview notification');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.body) {
      toast.error('Please fill in title and body');
      return;
    }
    setIsLoading(true);
    try {
      let scheduledAt: string | undefined = undefined;
      if (!sendNow && scheduledDate && scheduledTime) {
        const localDate = new Date(`${scheduledDate}T${scheduledTime}:00`);
        scheduledAt = localDate.toISOString();
      }
      const submitData: CreateNotificationDto = { ...formData, scheduled_at: scheduledAt };
      await notificationsApi.create(submitData);
      toast.success(sendNow ? 'Notification sent successfully' : 'Notification scheduled successfully');
      navigate('/notifications');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create notification');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/notifications')} className={cn(isDark ? 'text-slate-400 hover:text-white hover:bg-slate-800' : '')}>
          <ArrowLeft className="w-4 h-4 mr-2" />Back
        </Button>
        <div>
          <h1 className={cn('text-2xl lg:text-3xl font-bold tracking-tight', isDark ? 'text-white' : 'text-[#0F172A]')}>Create Notification</h1>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>Send push notifications to your users</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
            <div className="p-6 space-y-4">
              <h2 className={cn('text-lg font-semibold', isDark ? 'text-white' : 'text-[#0F172A]')}>Notification Details</h2>
              <div className="space-y-2">
                <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Title *</Label>
                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Notification title"
                  className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
              </div>
              <div className="space-y-2">
                <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Body *</Label>
                <Textarea value={formData.body} onChange={(e) => setFormData({ ...formData, body: e.target.value })} placeholder="Notification message" rows={4}
                  className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
              </div>
              <div className="space-y-2">
                <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Type</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value as NotificationType })}>
                  <SelectTrigger className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NotificationType.SYSTEM}>System</SelectItem>
                    <SelectItem value={NotificationType.PAYMENT}>Payment</SelectItem>
                    <SelectItem value={NotificationType.SUBSCRIPTION}>Subscription</SelectItem>
                    <SelectItem value={NotificationType.PROMO}>Promo</SelectItem>
                    <SelectItem value={NotificationType.REMINDER}>Reminder</SelectItem>
                    <SelectItem value={NotificationType.ACHIEVEMENT}>Achievement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Deep Link (optional)</Label>
                <Input value={formData.data?.deep_link || ''} onChange={(e) => setFormData({ ...formData, data: { ...formData.data, deep_link: e.target.value } })} placeholder="talabauz://Payment"
                  className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
              </div>
            </div>
          </Card>

          {/* Audience */}
          <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
            <div className="p-6 space-y-4">
              <h2 className={cn('text-lg font-semibold', isDark ? 'text-white' : 'text-[#0F172A]')}>Audience</h2>
              <div className="space-y-2">
                <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Audience Type</Label>
                <Select value={formData.audience_filters?.audience_type || AudienceType.ALL} onValueChange={(value) => setFormData({ ...formData, audience_filters: { ...formData.audience_filters, audience_type: value as AudienceType } })}>
                  <SelectTrigger className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={AudienceType.ALL}>All Users</SelectItem>
                    <SelectItem value={AudienceType.PREMIUM}>Premium Users</SelectItem>
                    <SelectItem value={AudienceType.FREE}>Free Users</SelectItem>
                    <SelectItem value={AudienceType.BY_PREFERENCES}>By Preferences</SelectItem>
                    <SelectItem value={AudienceType.BY_ACTIVITY}>By Activity</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.audience_filters?.audience_type === AudienceType.BY_ACTIVITY && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Days Active</Label>
                    <Input type="number" value={formData.audience_filters?.activity_filters?.days_active || ''} placeholder="30"
                      onChange={(e) => setFormData({ ...formData, audience_filters: { ...formData.audience_filters!, activity_filters: { ...formData.audience_filters?.activity_filters, days_active: e.target.value ? parseInt(e.target.value) : undefined } } })}
                      className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
                  </div>
                  <div className="space-y-2">
                    <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Days Inactive</Label>
                    <Input type="number" value={formData.audience_filters?.activity_filters?.days_inactive || ''} placeholder="30"
                      onChange={(e) => setFormData({ ...formData, audience_filters: { ...formData.audience_filters!, activity_filters: { ...formData.audience_filters?.activity_filters, days_inactive: e.target.value ? parseInt(e.target.value) : undefined } } })}
                      className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Schedule */}
          <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className={cn('text-lg font-semibold', isDark ? 'text-white' : 'text-[#0F172A]')}>Schedule</h2>
                <div className="flex items-center gap-2">
                  <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Send Now</Label>
                  <Switch checked={sendNow} onCheckedChange={setSendNow} />
                </div>
              </div>
              {!sendNow && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Date</Label>
                    <Input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
                  </div>
                  <div className="space-y-2">
                    <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Time</Label>
                    <Input type="time" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Options */}
          <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
            <div className="p-6 space-y-4">
              <h2 className={cn('text-lg font-semibold', isDark ? 'text-white' : 'text-[#0F172A]')}>Options</h2>
              <div className="flex items-center justify-between">
                <div>
                  <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Sponsored Notification</Label>
                  <p className={cn('text-xs mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>Mark as sponsored content</p>
                </div>
                <Switch checked={formData.is_sponsored} onCheckedChange={(checked) => setFormData({ ...formData, is_sponsored: checked })} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Premium Notification</Label>
                  <p className={cn('text-xs mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>Mark as premium content</p>
                </div>
                <Switch checked={formData.is_premium} onCheckedChange={(checked) => setFormData({ ...formData, is_premium: checked })} />
              </div>
              <div className="space-y-2">
                <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Priority (1-10)</Label>
                <Input type="number" min="1" max="10" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 5 })}
                  className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
              </div>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex items-center gap-4">
            <Button onClick={handlePreview} disabled={isPreviewLoading} variant="outline" className={cn(isDark ? 'border-slate-600 text-white hover:bg-slate-700' : '')}>
              {isPreviewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}Preview
            </Button>
            <Button onClick={handleSubmit} disabled={isLoading} className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90 flex-1">
              {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : sendNow ? <Send className="w-4 h-4 mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
              {sendNow ? 'Send Now' : 'Schedule'}
            </Button>
          </div>
        </div>

        {/* Preview */}
        <div className="lg:col-span-1">
          <Card className={cn('sticky top-24', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
            <div className="p-6 space-y-4">
              <h2 className={cn('text-lg font-semibold', isDark ? 'text-white' : 'text-[#0F172A]')}>Preview</h2>
              {previewData ? (
                <div className="space-y-4">
                  <div className={cn('p-4 rounded-lg', isDark ? 'bg-slate-700' : 'bg-slate-50')}>
                    <div className={cn('font-semibold mb-1', isDark ? 'text-white' : 'text-[#0F172A]')}>{formData.title}</div>
                    <div className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-600')}>{formData.body}</div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2"><Users className={cn('w-4 h-4', isDark ? 'text-slate-400' : 'text-slate-500')} /><span className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-600')}>Recipients</span></div>
                      <span className={cn('font-semibold', isDark ? 'text-white' : 'text-[#0F172A]')}>{previewData.estimated_recipients.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2"><TrendingUp className={cn('w-4 h-4', isDark ? 'text-slate-400' : 'text-slate-500')} /><span className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-600')}>Devices</span></div>
                      <span className={cn('font-semibold', isDark ? 'text-white' : 'text-[#0F172A]')}>{previewData.estimated_devices.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className={cn('p-3 rounded-lg text-sm', isDark ? 'bg-slate-700' : 'bg-slate-50')}>
                    <p className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>{previewData.message}</p>
                  </div>
                </div>
              ) : (
                <div className={cn('text-center py-8 text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>Click "Preview" to see estimated recipients</div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
